import React, { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import type { FileResponseItem } from '../models.ts';
import type { UploadItem } from '@services/uploadService.ts';
import { fileService } from '@services/fileService.ts';
import { uploadService } from '@services/uploadService.ts';
import { Timestamp } from '@components/Timestamp.tsx';
import { FileCardMenu } from '@components/FileCardMenu.tsx';
import { FileIcon } from '@components/FileIcon.tsx';
import { ImgShare } from '@components/Images/ImgShare.tsx';
import { ImgDownload } from '@components/Images/ImgDownload.tsx';
import { ImgCopy } from '@components/Images/ImgCopy.tsx';
import { ImgClose } from '@components/Images/ImgClose.tsx';
import { ImgCheckMark } from '@components/Images/ImgCheckMark.tsx';
import { getPreviewableType, getExpirationWarning } from '@root/utils/fileUtils.ts';

function formatFullDateTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

type FileListViewProps = {
  files: FileResponseItem[];
  activeUploads: UploadItem[];
  removingIds: Set<string>;
  renderText: (text: string | undefined | null) => React.ReactNode;
  onPreviewFile: (file: FileResponseItem) => void;
  onDeleteFile: (file: FileResponseItem) => void;
  onPermanentDelete: (file: FileResponseItem) => void;
  onManageExpiration: (file: FileResponseItem) => void;
  onCustomize: (file: FileResponseItem) => void;
  onShareCode: (file: FileResponseItem) => void;
  onReupload?: (file: FileResponseItem) => void;
  onCustomLink?: (file: FileResponseItem) => void;
};

export const FileListView = observer(function FileListView({
  files,
  activeUploads,
  removingIds,
  renderText,
  onPreviewFile,
  onDeleteFile,
  onPermanentDelete,
  onManageExpiration,
  onCustomize,
  onShareCode,
  onReupload,
  onCustomLink,
}: FileListViewProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyDownloadUrl = useCallback((e: React.MouseEvent, fileId: string, customName?: string | null) => {
    e.stopPropagation();
    const url = customName
      ? `${window.location.origin}/f/${customName}`
      : `${window.location.origin}/file/download/${fileId}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopiedId(fileId);
        setTimeout(() => setCopiedId(id => id === fileId ? null : id), 1500);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="file-list">
      {activeUploads.map(upload => (
        <div className="file-list-item file-list-item-uploading" key={'ul-' + upload.id}>
          <div className="file-list-item-icon">
            <FileIcon fileName={upload.fileName} />
          </div>
          <div className="file-list-item-content">
            <div className="file-list-item-row">
              <span className="file-list-item-title">{upload.fileName}</span>
              {upload.status !== 'registering' && (
                <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                  <button
                    className={`file-list-copy-btn${copiedId === upload.id ? ' copied' : ''}`}
                    onClick={(e) => copyDownloadUrl(e, upload.id)}
                  >
                    {copiedId === upload.id
                      ? <ImgCheckMark />
                      : <ImgCopy />}
                  </button>
                  {copiedId === upload.id && (
                    <span className="file-list-copied-popup">Copied!</span>
                  )}
                </span>
              )}
            </div>
            {upload.status === 'registering' ? (
              <div className="file-list-item-row">
                <span className="file-list-item-meta" style={{ opacity: 0.6 }}>Registering on server...</span>
              </div>
            ) : (
              <>
                <div className="file-list-item-row">
                  <span className="file-list-item-meta">
                    {fileService.formatFileSize(upload.loaded)} / {fileService.formatFileSize(upload.fileSize)}
                    {!!upload.bitrate && <span style={{ marginLeft: '6px', opacity: 0.7 }}>{uploadService.formatBitrate(upload.bitrate)}</span>}
                    {!!upload.timeLeftSeconds && <span style={{ marginLeft: '6px', opacity: 0.7 }}>{uploadService.formatTimeLeft(upload.timeLeftSeconds)} left</span>}
                  </span>
                </div>
                <div className="file-list-item-row">
                  <span className="file-list-item-meta">Uploading...</span>
                </div>
              </>
            )}
          </div>
          <div className="file-list-item-actions">
            {upload.status !== 'registering' && (
              <a
                className="action-btn"
                href={`/file/download/${upload.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open share page"
              >
                <ImgShare size={16} />
              </a>
            )}
            {(upload.status === 'uploading' || upload.status === 'registering') && (
              <button className="action-btn" title="Cancel upload" onClick={() => uploadService.cancelUpload(upload.id)}><ImgClose /></button>
            )}
          </div>
          <div className="file-list-progress-row">
            <div className="upload-list-progress-bar">
              {upload.status === 'registering'
                ? <div className="upload-card-progress-fill upload-card-progress-indeterminate" />
                : <div className="upload-card-progress-fill" style={{ width: `${upload.progress}%` }} />
              }
            </div>
          </div>
        </div>
      ))}
      {files.map(file => {
        const fileWarning = getExpirationWarning(file);
        const incompletePct = file.fileSize > 0 ? Math.round(((file.readySize || 0) / file.fileSize) * 100) : 0;
        const previewType = getPreviewableType(file.fileName);
        return (
          <div
            className={`file-list-item${removingIds.has(file.id) ? ' removing' : ''}${!file.isComplete ? ' file-list-item-uploading' : ''}`}
            key={'l' + file.id}
          >
            <div
              className="file-list-item-icon"
              onClick={previewType && file.isComplete ? () => onPreviewFile(file) : undefined}
              style={previewType && file.isComplete ? { cursor: 'pointer' } : undefined}
            >
              <FileIcon fileName={file.fileName} fileId={file.id} file={file} view="list" />
            </div>
            <div className="file-list-item-content">
              <div className="file-list-item-row">
                <a
                  className="file-list-item-title file-list-item-title-link"
                  href={file.customName ? `/f/${file.customName}` : `/file/download/${file.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderText(file.fileName)}
                </a>
                {file.isComplete && (
                  <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, top: 2 }}>
                    <button
                      className={`file-list-copy-btn${copiedId === file.id ? ' copied' : ''}`}
                      onClick={(e) => copyDownloadUrl(e, file.id, file.customName)}
                    >
                      {copiedId === file.id
                        ? <ImgCheckMark />
                        : <ImgCopy />}
                    </button>
                    {copiedId === file.id && (
                      <span className="file-list-copied-popup">Copied!</span>
                    )}
                  </span>
                )}
                {fileWarning && <span title={fileWarning} style={{ color: 'var(--warning, #f59e0b)', marginLeft: '4px', cursor: 'help' }}>*</span>}
                {!file.isComplete && <span style={{ marginLeft: '8px', color: 'var(--warning-color)', fontSize: '12px' }}>Incomplete ({incompletePct}%)</span>}
              </div>
              <div className="file-list-item-row">
                <span className="file-list-item-meta">
                  {!file.isComplete
                    ? `${fileService.formatFileSize(file.readySize || 0)} / ${fileService.formatFileSize(file.fileSize)}`
                    : (file.zipFullSize ?? 0) > 0
                      ? fileService.formatFileSize(file.zipFullSize)
                      : fileService.formatFileSize(file.fileSize)}
                </span>
                {(file.zipTotalFiles ?? 0) > 0
                  ? <span className="file-list-item-meta" style={{ marginLeft: '8px' }}>{file.zipTotalFiles} files</span>
                  : null}
              </div>
              <div className="file-list-item-row">
                {file.dateTime && <Timestamp timestamp={file.dateTime} short />}
                {file.lastRequest && (
                  <span className="file-list-last-dl">
                    <ImgDownload size={12} />
                    <Timestamp timestamp={file.lastRequest} short />
                    <span className="file-list-last-dl-popup">{formatFullDateTime(file.lastRequest)}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="file-list-item-actions">
              <FileCardMenu
                file={file}
                onDelete={() => onDeleteFile(file)}
                onPermanentDelete={() => onPermanentDelete(file)}
                onManageExpiration={() => onManageExpiration(file)}
                onCustomize={() => onCustomize(file)}
                onShareCode={() => onShareCode(file)}
                onReupload={onReupload ? () => onReupload(file) : undefined}
                onCustomLink={onCustomLink ? () => onCustomLink(file) : undefined}
              />
            </div>
            {!file.isComplete && (
              <div className="file-list-progress-row">
                <div className="upload-list-progress-bar">
                  <div className="upload-card-progress-fill" style={{ width: `${incompletePct}%` }} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});
